import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { Achievement } from '../lib/types';
import { Link } from 'react-router-dom';
import { Bar, EmptyState, ErrorState, LoadingGrid, Panel, Tabs } from '../components/ui';
import { formatNumber } from '../lib/format';

/**
 * Achievements: the moments.
 *
 * Everything with a ladder moved to /badges. What is left is one-time
 * milestones — your first hatch, your first Guild, the first Pokemon you fed
 * for somebody else — and the "x of y" counter now only counts those, so it is
 * a number a player can actually finish.
 *
 * The retired ladder rungs are not gone. Any that were earned appear in their
 * own section at the foot of the page, marked with the badge that took over.
 * Deleting them would have been simpler and would have wiped an unlock off
 * every profile in the game that had one.
 */

const BADGE_ART: Record<string, string> = {
  egg: '◓', dex: '▤', sparkle: '✦', heart: '♥', crown: '♛', star: '★', type: '◈', evolve: '⟳',
};

export function AchievementsPage() {
  const state = useAsync<{ achievements: Achievement[] }>(() => api.get('/me/achievements'));
  const [filter, setFilter] = useState<'all' | 'progress' | 'done'>('all');

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const rows = state.data?.achievements ?? [];
  // Live milestones vs. rungs of the old ladder that a badge now owns.
  const all = rows.filter((entry) => !entry.supersededBy);
  const retired = rows.filter((entry) => entry.supersededBy && entry.completed);
  const completed = all.filter((entry) => entry.completed);

  const visible = all.filter((entry) =>
    filter === 'all' ? true : filter === 'done' ? entry.completed : !entry.completed,
  );

  const categories = [...new Set(visible.map((entry) => entry.category))];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Achievements</h1>
          <div className="page-head__sub">
            {completed.length} of {all.length} unlocked · one-time milestones
          </div>
        </div>
      </div>

      {all.length > 0 && (
        <Panel raised={false}>
          <div className="stack-sm">
            <Bar ratio={completed.length / Math.max(1, all.length)} tall variant="ready" />
            <Tabs
              value={filter}
              onChange={setFilter}
              tabs={[
                { value: 'all', label: 'All' },
                { value: 'progress', label: 'In progress', badge: all.length - completed.length },
                { value: 'done', label: 'Unlocked', badge: completed.length },
              ]}
            />
          </div>
        </Panel>
      )}

      {state.loading ? (
        <LoadingGrid count={8} height={110} />
      ) : (
        categories.map((category) => (
          <Panel key={category} title={category}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {visible
                .filter((entry) => entry.category === category)
                .map((achievement) => (
                  <div
                    key={achievement.key}
                    className="pxwell"
                    style={{
                      padding: 'var(--gap-sm)',
                      opacity: achievement.completed ? 1 : 0.92,
                      background: achievement.completed ? 'var(--paper-warm)' : undefined,
                    }}
                  >
                    <div className="row" style={{ gap: 'var(--gap-sm)', alignItems: 'flex-start' }}>
                      <span
                        style={{
                          fontSize: '1.7rem',
                          lineHeight: 1,
                          color: achievement.completed ? 'var(--gold-dark)' : 'var(--ink-faint)',
                          filter: achievement.completed ? 'none' : 'grayscale(1)',
                        }}
                        aria-hidden
                      >
                        {BADGE_ART[achievement.badge] ?? '★'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="row row--between">
                          <span className="mono tiny">{achievement.name}</span>
                          {achievement.completed && (
                            <span className="tiny mono" style={{ color: 'var(--leaf-dark)' }}>✓</span>
                          )}
                        </div>
                        <p className="tiny muted" style={{ margin: '2px 0 6px' }}>
                          {achievement.description}
                        </p>

                        {!achievement.completed && (
                          <>
                            <Bar ratio={achievement.progress / achievement.goal} />
                            <div className="tiny muted mono" style={{ marginTop: 3 }}>
                              {formatNumber(achievement.progress)} / {formatNumber(achievement.goal)}
                            </div>
                          </>
                        )}

                        {(achievement.rewardCoins > 0 || achievement.rewardTokens > 0 || achievement.rewardTitle) && (
                          <div className="row row--wrap tiny" style={{ gap: 8, marginTop: 6 }}>
                            {achievement.rewardCoins > 0 && (
                              <span className="row" style={{ gap: 4 }}>
                                <span className="coin__dot coin__dot--gold" aria-hidden />
                                {formatNumber(achievement.rewardCoins)}
                              </span>
                            )}
                            {achievement.rewardTokens > 0 && (
                              <span className="row" style={{ gap: 4 }}>
                                <span className="coin__dot coin__dot--token" aria-hidden />
                                {formatNumber(achievement.rewardTokens)}
                              </span>
                            )}
                            {achievement.rewardTitle && (
                              <span className="chip">Title: {achievement.rewardTitle}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Panel>
        ))
      )}

      {retired.length > 0 && (
        <Panel
          title="◷ Earned before badges"
          hint="These are now tracked as badges"
          action={
            <Link to="/badges" className="btn btn--sm">
              View badges
            </Link>
          }
        >
          <p className="tiny muted" style={{ marginTop: 0 }}>
            These used to be achievements with tiers. They are yours forever — the axis each one
            measured is now a badge that keeps counting.
          </p>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {retired.map((achievement) => (
              <div
                key={achievement.key}
                className="pxwell row"
                style={{ padding: 'var(--gap-sm)', gap: 'var(--gap-sm)', alignItems: 'flex-start' }}
              >
                <span style={{ fontSize: '1.3rem', lineHeight: 1, color: 'var(--gold-dark)' }} aria-hidden>
                  {BADGE_ART[achievement.badge] ?? '★'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="mono tiny">{achievement.name}</div>
                  <div className="tiny muted">{achievement.description}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {!state.loading && all.length > 0 && completed.length === all.length && (
        <EmptyState art="★" title="Every milestone unlocked">
          Nothing one-time left to do. The long game lives on the Badges page.
        </EmptyState>
      )}
    </div>
  );
}
